import { useState } from "react";
import { StarIcon } from "@/assets/icons";
import { toast } from "@/components/ui/toast";
import { Square2StackIcon } from "@heroicons/react/20/solid";

/* Prop Types */
interface VideoCopilotScenePanelProps {
  analysis: string;
}

/**
 * VideoCopilotScenePanel component
 *
 * Scene-specific Copilot analysis panel shown alongside a selected scene's metrics, with
 * copy-to-clipboard and a show more/less toggle for longer analyses.
 */
export const VideoCopilotScenePanel = ({ analysis }: VideoCopilotScenePanelProps) => {
  /* State */
  const [isExpanded, setIsExpanded] = useState(false);

  /* Handlers */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(analysis);
      toast({ title: "Analysis copied to clipboard", type: "success" });
    } catch {
      toast({ title: "Failed to copy analysis, please try again", type: "error" });
    }
  };

  /* Render */
  return (
    <div data-theme="light" className="flex flex-col gap-2 p-4 rounded-lg bg-linear-to-br from-violet-800 to-pink-600 text-white">
      <div className="flex items-center gap-1.5">
        <StarIcon className="size-4" />
        <span className="flex-1 text-sm font-semibold">Copilot</span>
        <button type="button" onClick={handleCopy} aria-label="Copy analysis" className="flex items-center justify-center size-[26px] rounded-xs shrink-0 cursor-pointer hover:bg-white/10">
          <Square2StackIcon className="size-4.5" />
        </button>
      </div>
      <p className={isExpanded ? "text-sm" : "text-sm line-clamp-3"}>{analysis}</p>
      <button type="button" onClick={() => setIsExpanded((prev) => !prev)} className="self-start text-sm font-semibold cursor-pointer hover:underline">
        {isExpanded ? "Show less" : "Show more"}
      </button>
    </div>
  );
};
